import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Check, 
  Shirt, Sofa, Gem, UtensilsCrossed, Ticket, Plane,
  Zap
} from 'lucide-react';

const STEPS = ['Category', 'Filters', 'Brands', 'Threshold', 'Review'];

const CATEGORY_DATA = {
  clothing: {
    name: 'Clothing',
    icon: Shirt,
    color: '#F472B6',
    subcategories: ['Shirts', 'Pants', 'Shorts', 'Formal Wear', 'Casual Wear', 'Gym Wear', 'Suits', 'Blazers', 'Accessories'],
    filters: { gender: ['Men', 'Women', 'Unisex'] },
    brands: ['Nike', 'Adidas', 'Puma', 'Lululemon', 'Uniqlo', 'Zara', 'H&M', 'Nordstrom', 'Neiman Marcus', 'SSENSE', 'Gap', 'Old Navy', 'Banana Republic', 'J.Crew', 'Ralph Lauren']
  },
  furniture: {
    name: 'Furniture',
    icon: Sofa,
    color: '#0D9488',
    subcategories: ['Sofa', 'Bed', 'Dining', 'Storage', 'Office', 'Outdoor'],
    filters: { material: ['Wood', 'Metal', 'Fabric', 'Leather'] },
    brands: ['IKEA', 'West Elm', 'Restoration Hardware', 'CB2', 'Wayfair', 'Crate & Barrel', 'Pottery Barn', 'Ashley Furniture', 'Ethan Allen']
  },
  jewelry: {
    name: 'Jewelry',
    icon: Gem,
    color: '#FACC15',
    subcategories: ['Rings', 'Necklaces', 'Bracelets', 'Earrings', 'Watches'],
    filters: { material: ['Gold', 'Silver', 'Platinum', 'Diamond'] },
    brands: ['Tiffany & Co', 'Cartier', 'Pandora', 'Swarovski', 'Kay Jewelers', 'Blue Nile', 'James Allen', 'Brilliant Earth']
  },
  food: {
    name: 'Food',
    icon: UtensilsCrossed,
    color: '#FB923C',
    subcategories: ['Restaurants', 'Delivery', 'Special Events', 'Chef Tastings'],
    filters: { cuisine: ['Italian', 'Asian', 'Mexican', 'American', 'French'] },
    brands: ['OpenTable', 'Resy', 'DoorDash', 'Uber Eats', 'Grubhub', 'Postmates', 'Yelp']
  },
  experience: {
    name: 'Experience',
    icon: Ticket,
    color: '#A855F7',
    subcategories: ['Concerts', 'Sports', 'Workshops', 'Retreats', 'Theater'],
    filters: { type: ['Music', 'Sports', 'Arts', 'Wellness'] },
    brands: ['Ticketmaster', 'Eventbrite', 'StubHub', 'SeatGeek', 'Live Nation', 'AXS']
  },
  travel: {
    name: 'Travel',
    icon: Plane,
    color: '#60A5FA',
    subcategories: ['Flights', 'Hotels', 'Vacation Packages', 'Car Rentals'],
    filters: { class: ['Economy', 'Business', 'First Class'] },
    brands: ['Google Flights', 'Skyscanner', 'Expedia', 'Kayak', 'Booking.com', 'Airbnb', 'Hotels.com', 'Priceline', 'Hopper']
  }
};

const CreateTracker = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [trackerName, setTrackerName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [trackAllBrands, setTrackAllBrands] = useState(false);
  const [discountThreshold, setDiscountThreshold] = useState(30);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyInApp, setNotifyInApp] = useState(true);

  const categoryData = selectedCategory ? CATEGORY_DATA[selectedCategory] : null;

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedCategory !== '';
      case 1: return true; // Filters are optional
      case 2: return trackAllBrands || selectedBrands.length > 0;
      case 3: return discountThreshold >= 5;
      case 4: return trackerName.trim() !== '';
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async () => {
    if (!trackerName.trim()) {
      toast.error('Please enter a tracker name');
      return;
    }

    setLoading(true);
    try {
      await api.post('/trackers', {
        name: trackerName,
        category: selectedCategory,
        subcategory: selectedSubcategory || null,
        filters: selectedFilters,
        brands: trackAllBrands ? [] : selectedBrands,
        discount_threshold: discountThreshold,
        notify_email: notifyEmail,
        notify_inapp: notifyInApp,
      });
      toast.success('Tracker created! We\'ll notify you when deals drop.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tracker');
    } finally {
      setLoading(false);
    }
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const getSliderBackground = (value) => {
    const percentage = ((value - 5) / 90) * 100;
    return `linear-gradient(90deg, #34D399 0%, #F472B6 ${percentage}%, #27272A ${percentage}%)`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              <span className="font-heading font-bold">New Tracker</span>
            </div>
            <div className="w-16" /> {/* Spacer */}
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div key={step} className="flex items-center flex-1">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  index < currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : index === currentStep 
                      ? 'bg-primary/20 text-primary border-2 border-primary' 
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 transition-all ${index < currentStep ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <AnimatePresence mode="wait">
          {/* Step 0: Category */}
          {currentStep === 0 && (
            <motion.div
              key="category"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">What are you hunting today?</h2>
                <p className="text-muted-foreground">Choose a category to track deals in</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(CATEGORY_DATA).map(([key, category]) => (
                  <motion.button
                    key={key}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedCategory(key);
                      setSelectedSubcategory('');
                      setSelectedFilters({});
                      setSelectedBrands([]);
                    }}
                    className={`glass rounded-2xl p-6 text-left transition-all ${
                      selectedCategory === key 
                        ? 'border-2 border-primary shadow-glow-pink' 
                        : 'border border-transparent hover:border-border'
                    }`}
                    data-testid={`category-${key}`}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <category.icon className="w-6 h-6" style={{ color: category.color }} />
                    </div>
                    <h3 className="font-semibold">{category.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {category.brands.length} brands
                    </p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 1: Filters */}
          {currentStep === 1 && categoryData && (
            <motion.div
              key="filters"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">Refine your search</h2>
                <p className="text-muted-foreground">Optional: narrow down what you're looking for</p>
              </div>

              {/* Subcategory */}
              <div className="space-y-3">
                <Label className="text-sm text-muted-foreground">Type (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {categoryData.subcategories.map((sub) => (
                    <button
                      key={sub}
                      onClick={() => setSelectedSubcategory(selectedSubcategory === sub ? '' : sub)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        selectedSubcategory === sub
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                      data-testid={`subcategory-${sub}`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dynamic Filters */}
              {Object.entries(categoryData.filters).map(([filterKey, filterOptions]) => (
                <div key={filterKey} className="space-y-3">
                  <Label className="text-sm text-muted-foreground capitalize">{filterKey} (optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => setSelectedFilters(prev => ({
                          ...prev,
                          [filterKey]: prev[filterKey] === option ? null : option
                        }))}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          selectedFilters[filterKey] === option
                            ? 'bg-secondary text-secondary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                        data-testid={`filter-${filterKey}-${option}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Step 2: Brands */}
          {currentStep === 2 && categoryData && (
            <motion.div
              key="brands"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">Pick your brands</h2>
                <p className="text-muted-foreground">Select specific brands or track them all</p>
              </div>

              {/* Track All Toggle */}
              <div className="glass rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <p className="font-medium">Track All Major Brands</p>
                  <p className="text-sm text-muted-foreground">Get notified for any brand in this category</p>
                </div>
                <Switch 
                  checked={trackAllBrands}
                  onCheckedChange={setTrackAllBrands}
                  data-testid="track-all-brands-switch"
                />
              </div>

              {!trackAllBrands && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {selectedBrands.length} selected
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categoryData.brands.map((brand) => (
                      <button
                        key={brand}
                        onClick={() => toggleBrand(brand)}
                        className={`glass rounded-xl p-4 text-left transition-all flex items-center gap-3 ${
                          selectedBrands.includes(brand)
                            ? 'border-2 border-primary'
                            : 'border border-transparent hover:border-border'
                        }`}
                        data-testid={`brand-${brand}`}
                      >
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                          selectedBrands.includes(brand)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}>
                          {selectedBrands.includes(brand) && <Check className="w-3 h-3" />}
                        </div>
                        <span className="font-medium text-sm">{brand}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Threshold */}
          {currentStep === 3 && (
            <motion.div
              key="threshold"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">Set your target discount</h2>
                <p className="text-muted-foreground">We'll only notify you when deals hit this threshold</p>
              </div>

              <div className="glass rounded-3xl p-8 space-y-6">
                <div className="text-center">
                  <span className="text-6xl font-heading font-bold text-gradient">{discountThreshold}%</span>
                  <p className="text-muted-foreground mt-2">or more off</p>
                </div>

                <input 
                  type="range"
                  min="5"
                  max="95"
                  value={discountThreshold}
                  onChange={(e) => setDiscountThreshold(parseInt(e.target.value))}
                  className="discount-slider w-full h-3 rounded-full cursor-pointer"
                  style={{ background: getSliderBackground(discountThreshold) }}
                  data-testid="discount-slider"
                />

                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5%</span>
                  <span>50%</span>
                  <span>95%</span>
                </div>

                <div className="p-4 rounded-xl bg-card/50 border border-border/50">
                  <p className="text-sm text-muted-foreground text-center">
                    {discountThreshold < 20 && "You'll get frequent notifications for small discounts."}
                    {discountThreshold >= 20 && discountThreshold < 40 && "Good balance! You'll catch most sales."}
                    {discountThreshold >= 40 && discountThreshold < 60 && "Patient hunter! Waiting for solid deals."}
                    {discountThreshold >= 60 && discountThreshold < 80 && "Only the big drops for you! 🎯"}
                    {discountThreshold >= 80 && "Legendary patience! Only massive discounts."}
                  </p>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-4">
                <h3 className="font-semibold">Notification preferences</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between glass rounded-xl p-4">
                    <div>
                      <p className="font-medium">In-App Notifications</p>
                      <p className="text-sm text-muted-foreground">Get notified in the app</p>
                    </div>
                    <Switch 
                      checked={notifyInApp}
                      onCheckedChange={setNotifyInApp}
                      data-testid="notify-inapp-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between glass rounded-xl p-4">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-muted-foreground">Receive deal alerts via email</p>
                    </div>
                    <Switch 
                      checked={notifyEmail}
                      onCheckedChange={setNotifyEmail}
                      data-testid="notify-email-switch"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && categoryData && (
            <motion.div
              key="review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">Name your tracker</h2>
                <p className="text-muted-foreground">Give it a memorable name</p>
              </div>

              <div className="space-y-2">
                <Input 
                  type="text"
                  placeholder={`e.g., "Nike sneaker deals" or "Weekend flights"`}
                  value={trackerName}
                  onChange={(e) => setTrackerName(e.target.value)}
                  className="h-14 text-lg bg-input/50 border-transparent focus:border-primary rounded-xl"
                  data-testid="tracker-name-input"
                />
              </div>

              <div className="glass rounded-2xl p-6 space-y-4">
                <h3 className="font-semibold">Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium capitalize">{categoryData.name}</span>
                  </div>
                  {selectedSubcategory && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">{selectedSubcategory}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Brands</span>
                    <span className="font-medium">
                      {trackAllBrands ? 'All brands' : `${selectedBrands.length} selected`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount threshold</span>
                    <span className="font-medium text-success">{discountThreshold}%+ off</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Notifications</span>
                    <span className="font-medium">
                      {[notifyInApp && 'In-app', notifyEmail && 'Email'].filter(Boolean).join(', ') || 'None'}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 glass border-t border-border/50 p-4">
        <div className="max-w-3xl mx-auto flex gap-4">
          <Button 
            variant="outline"
            onClick={handleBack}
            className="flex-1 h-12 rounded-xl"
            data-testid="prev-btn"
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground shadow-glow-pink hover:shadow-[0_0_30px_rgba(244,114,182,0.4)] transition-all"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground shadow-glow-pink hover:shadow-[0_0_30px_rgba(244,114,182,0.4)] transition-all"
              data-testid="create-tracker-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Tracker
                  <Check className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTracker;
